import React, {
  useEffect, useRef, useState,
} from 'react';

import {
  colorForLabel, mapPool, tileBoxesKey,
} from './helpers';
import { resolveAssetUrl } from './signedAssetCache';
import style from './style.module.scss';

const PREVIEW = (tile) => tile?.webp || tile?.url || null;
const FULL = (tile) => tile?.url || null;
const LOAD_CONCURRENCY = 10;
const DEFAULT_TW = 1280;
const DEFAULT_TH = 720;
/**
 * Browser/GPU canvas texture limit. Full cols×1280 mosaics often exceed this
 * and silently fail or thrash. We keep full-res Image bitmaps in RAM and blit
 * them into a GPU-safe atlas (highest fidelity under the cap).
 */
const MAX_CANVAS_EDGE = 8192;

const loadImage = (url) => new Promise((resolve, reject) => {
  if (!url) {
    reject(new Error('no url'));
    return;
  }
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('img error'));
  img.src = url;
});

const tileKeyOf = (tile) => `${tile.row}-${tile.col}-${tile.index}`;

const sourceCellSize = (imgs, list) => {
  const hit = (list || []).find((tile) => {
    const img = imgs.get(tileKeyOf(tile));
    return img?.naturalWidth > 0 && img?.naturalHeight > 0;
  });
  if (!hit) return { tw: DEFAULT_TW, th: DEFAULT_TH };
  const img = imgs.get(tileKeyOf(hit));
  return { tw: img.naturalWidth, th: img.naturalHeight };
};

/** AWS BoundingBox % → hollow strokeRect in atlas pixels. */
const paintDetectionFrames = (ctx, list, cellW, cellH, tileBoxes, selectedLabels, boxKeyPrefix) => {
  if (!selectedLabels?.size || !tileBoxes) return;
  const stroke = Math.max(3, Math.min(cellW, cellH) * 0.007);
  list.forEach((tile) => {
    const boxes = tileBoxes[tileBoxesKey(tile, boxKeyPrefix)] || [];
    const dx = (Number(tile.col) || 0) * cellW;
    const dy = (Number(tile.row) || 0) * cellH;
    boxes.forEach((box) => {
      if (!box?.BoundingBox || !selectedLabels.has(box.label)) return;
      const {
        Left = 0, Top = 0, Width = 0, Height = 0,
      } = box.BoundingBox;
      const bw = Width * cellW;
      const bh = Height * cellH;
      if (bw < 0.5 || bh < 0.5) return;
      ctx.strokeStyle = colorForLabel(box.label);
      ctx.lineWidth = stroke;
      ctx.strokeRect(dx + Left * cellW, dy + Top * cellH, bw, bh);
    });
  });
};

/**
 * Region mosaic atlas. Sources stay full 720p WebP in memory; atlas is capped
 * for the GPU. Zoom is CSS transform on the Map (no canvas rewrite mid-gesture).
 *
 * Tiles are stretched (not cover-cropped) so AWS BoundingBox % match the cell.
 * Detection frames are stroked in atlas pixels (stay hollow under Map zoom).
 */
const MosaicCanvas = ({
  tiles,
  rows,
  cols,
  loading = false,
  tileBoxes = null,
  selectedLabels = null,
  showBoxes = false,
  boxKeyPrefix = null,
}) => {
  const canvasRef = useRef(null);
  const imagesRef = useRef(new Map());
  const genRef = useRef(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const list = tiles || [];
    const r = Number(rows) || 0;
    const c = Number(cols) || 0;
    if (loading || !list.length || r < 1 || c < 1) {
      imagesRef.current = new Map();
      setReady(false);
      return undefined;
    }

    let cancelled = false;
    const gen = genRef.current + 1;
    genRef.current = gen;
    setReady(false);

    (async () => {
      const next = new Map();
      await mapPool(list, LOAD_CONCURRENCY, async (tile) => {
        if (cancelled || gen !== genRef.current) return;
        const key = tileKeyOf(tile);
        const signed = PREVIEW(tile);
        if (!signed) {
          next.set(key, null);
          return;
        }
        try {
          let url = await resolveAssetUrl(signed);
          try {
            next.set(key, await loadImage(url));
          } catch {
            const full = FULL(tile);
            if (full && full !== signed) {
              url = await resolveAssetUrl(full);
              next.set(key, url ? await loadImage(url) : null);
            } else {
              next.set(key, null);
            }
          }
        } catch {
          next.set(key, null);
        }
      });
      if (cancelled || gen !== genRef.current) return;
      imagesRef.current = next;
      setReady(true);
    })();

    return () => { cancelled = true; };
  }, [tiles, rows, cols, loading]);

  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const r = Number(rows) || 0;
    const c = Number(cols) || 0;
    const list = tiles || [];
    if (r < 1 || c < 1) return;

    const imgs = imagesRef.current;
    const { tw, th } = sourceCellSize(imgs, list);
    const nativeW = c * tw;
    const nativeH = r * th;
    const fit = Math.min(1, MAX_CANVAS_EDGE / nativeW, MAX_CANVAS_EDGE / nativeH);
    const w = Math.max(1, Math.floor(nativeW * fit));
    const h = Math.max(1, Math.floor(nativeH * fit));
    const cellW = w / c;
    const cellH = h / r;

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#1a1d22';
    ctx.fillRect(0, 0, w, h);

    list.forEach((tile) => {
      const key = tileKeyOf(tile);
      const img = imgs.get(key);
      const dx = (Number(tile.col) || 0) * cellW;
      const dy = (Number(tile.row) || 0) * cellH;
      if (!img) {
        ctx.fillStyle = '#2a2e35';
        ctx.fillRect(dx, dy, cellW, cellH);
        return;
      }
      // Full image → cell (no cover crop) so BoundingBox % align.
      ctx.drawImage(img, dx, dy, cellW, cellH);
    });

    if (showBoxes) {
      paintDetectionFrames(ctx, list, cellW, cellH, tileBoxes, selectedLabels, boxKeyPrefix);
    }
  }, [
    ready, tiles, rows, cols, showBoxes, tileBoxes, selectedLabels, boxKeyPrefix,
  ]);

  return (
    <div className={style.mosaicLayer} aria-hidden>
      <canvas ref={canvasRef} className={style.mosaicCanvas} />
      {loading || !ready ? <div className={style.mosaicLoading} /> : null}
    </div>
  );
};

export default MosaicCanvas;
